/**
 * TourOverlay — Section-based tutorial overlay.
 *
 * Two modes:
 * 1. Section Selector: centered card with grid of section cards (when showSelector is true)
 * 2. Spotlight: highlight target element with positioned tooltip (when a section is active)
 *
 * Preserves the existing spotlight/tooltip/arrow rendering from the original flat tour.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Typography, Button, IconButton, Chip, LinearProgress, Paper } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  NavigateNext, NavigateBefore, Close, PlayArrow,
  CheckCircle, ArrowBack,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTour } from '../../contexts/TourContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { TOUR_SECTIONS } from './tourSteps';
import { BRAND, TEXT } from '../../theme/colors';

// ── Tooltip positioning logic ──────────────────────────────────────────
const TOOLTIP_W = 370;
const TOOLTIP_H = 210;
const GAP = 16;

const positionTooltip = (rect, preference) => {
  if (!rect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', arrow: 'none' };

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const attempts = [preference, 'right', 'left', 'bottom', 'top'];
  for (const pos of attempts) {
    if (pos === 'right' && rect.right + GAP + TOOLTIP_W < vw - 20) {
      return {
        top: Math.max(20, Math.min(rect.top + rect.height / 2 - TOOLTIP_H / 2, vh - TOOLTIP_H - 20)),
        left: rect.right + GAP,
        arrow: 'left',
      };
    }
    if (pos === 'left' && rect.left - GAP - TOOLTIP_W > 20) {
      return {
        top: Math.max(20, Math.min(rect.top + rect.height / 2 - TOOLTIP_H / 2, vh - TOOLTIP_H - 20)),
        left: rect.left - GAP - TOOLTIP_W,
        arrow: 'right',
      };
    }
    if (pos === 'bottom' && rect.bottom + GAP + TOOLTIP_H < vh - 20) {
      return {
        top: rect.bottom + GAP,
        left: Math.max(20, Math.min(rect.left + rect.width / 2 - TOOLTIP_W / 2, vw - TOOLTIP_W - 20)),
        arrow: 'top',
      };
    }
    if (pos === 'top' && rect.top - GAP - TOOLTIP_H > 20) {
      return {
        top: rect.top - GAP - TOOLTIP_H,
        left: Math.max(20, Math.min(rect.left + rect.width / 2 - TOOLTIP_W / 2, vw - TOOLTIP_W - 20)),
        arrow: 'bottom',
      };
    }
  }

  return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', arrow: 'none' };
};

// ── Arrow SVG ──────────────────────────────────────────────────────────
const ArrowSvg = ({ direction, color }) => {
  const size = 12;
  const style = { position: 'absolute' };

  if (direction === 'left') {
    Object.assign(style, { left: -size, top: '50%', transform: 'translateY(-50%)' });
    return <svg style={style} width={size} height={size * 2} viewBox="0 0 12 24"><polygon points="12,0 0,12 12,24" fill={color} /></svg>;
  }
  if (direction === 'right') {
    Object.assign(style, { right: -size, top: '50%', transform: 'translateY(-50%)' });
    return <svg style={style} width={size} height={size * 2} viewBox="0 0 12 24"><polygon points="0,0 12,12 0,24" fill={color} /></svg>;
  }
  if (direction === 'top') {
    Object.assign(style, { top: -size, left: '50%', transform: 'translateX(-50%)' });
    return <svg style={style} width={size * 2} height={size} viewBox="0 0 24 12"><polygon points="0,12 12,0 24,12" fill={color} /></svg>;
  }
  if (direction === 'bottom') {
    Object.assign(style, { bottom: -size, left: '50%', transform: 'translateX(-50%)' });
    return <svg style={style} width={size * 2} height={size} viewBox="0 0 24 12"><polygon points="0,0 12,12 24,0" fill={color} /></svg>;
  }
  return null;
};


// ── Section Selector Card ─────────────────────────────────────────────
const SectionSelector = ({ onStartSection, onPlayAll, onClose, completedSections, t }) => (
  <Box sx={{
    position: 'fixed', inset: 0, zIndex: 9998,
    bgcolor: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    animation: 'tourFadeIn 0.3s ease',
    '@keyframes tourFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
  }}>
    <Box sx={{
      width: { xs: '94vw', sm: 640, md: 720 },
      maxHeight: '85vh',
      bgcolor: '#fff',
      borderRadius: 4,
      overflow: 'hidden',
      boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
      animation: 'tourSlideUp 0.35s ease',
      '@keyframes tourSlideUp': { from: { opacity: 0, transform: 'translateY(24px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${BRAND.green} 0%, #059669 100%)`,
        color: '#fff', px: 3, py: 2.5,
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', lineHeight: 1.3 }}>
            {t('tourSelectorTitle') || 'TerraMind Tour'}
          </Typography>
          <Typography sx={{ fontSize: '0.92rem', opacity: 0.85, mt: 0.5 }}>
            {t('tourSelectorSubtitle') || 'Pick a section or start the full walkthrough'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
          <Close />
        </IconButton>
      </Box>

      {/* Section grid */}
      <Box sx={{ px: 3, py: 2.5, overflow: 'auto', flex: 1 }}>
        <Grid container spacing={2}>
          {TOUR_SECTIONS.map((section) => {
            const Icon = section.icon;
            const isCompleted = completedSections.has(section.id);
            const stepCount = section.steps.length;

            return (
              <Grid key={section.id} size={{ xs: 6, md: 4 }}>
                <Paper
                  onClick={() => onStartSection(section.id)}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: '1.5px solid',
                    borderColor: isCompleted ? '#d1fae5' : 'rgba(0,0,0,0.08)',
                    bgcolor: isCompleted ? '#f0fdf4' : '#fafafa',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: section.color,
                      boxShadow: `0 4px 16px ${section.color}22`,
                      transform: 'translateY(-2px)',
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    minHeight: 120,
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  {/* Completion checkmark */}
                  {isCompleted && (
                    <CheckCircle sx={{
                      position: 'absolute', top: 8, right: 8,
                      fontSize: 20, color: '#059669',
                    }} />
                  )}

                  <Box sx={{
                    width: 44, height: 44, borderRadius: '50%',
                    bgcolor: `${section.color}14`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon sx={{ fontSize: 24, color: section.color }} />
                  </Box>

                  <Typography sx={{
                    fontWeight: 600, fontSize: '0.95rem', color: TEXT.primary,
                    textAlign: 'center', lineHeight: 1.3,
                  }}>
                    {t(section.titleKey) || section.titleFallback}
                  </Typography>

                  <Chip
                    label={`${stepCount} ${stepCount === 1 ? (t('tourStep') || 'step') : (t('tourSteps') || 'steps')}`}
                    size="small"
                    sx={{
                      bgcolor: `${section.color}12`, color: section.color,
                      fontWeight: 600, fontSize: '0.92rem', height: 22,
                    }}
                  />
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Footer */}
      <Box sx={{
        px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
        borderTop: '1px solid rgba(0,0,0,0.06)',
      }}>
        <Button size="small" onClick={onClose}
          sx={{ color: 'text.secondary', textTransform: 'none', fontSize: '0.92rem' }}>
          {t('tourClose') || 'Close'}
        </Button>
        <Box sx={{ flex: 1 }} />
        {completedSections.size > 0 && (
          <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', mr: 1 }}>
            {completedSections.size}/{TOUR_SECTIONS.length}
          </Typography>
        )}
        <Button
          variant="contained"
          startIcon={<PlayArrow />}
          onClick={onPlayAll}
          sx={{
            bgcolor: BRAND.green, '&:hover': { bgcolor: BRAND.greenDark },
            textTransform: 'none', fontWeight: 600, fontSize: '0.92rem',
            px: 3, borderRadius: 2, boxShadow: 'none',
          }}
        >
          {t('tourPlayAll') || 'Full Tour'}
        </Button>
      </Box>
    </Box>
  </Box>
);

// ── Main overlay component ─────────────────────────────────────────────
const TourOverlay = () => {
  const {
    tourActive, showSelector, activeSection, sectionStep,
    completedSections, playAllMode, currentStep,
    tourStep,
    startSection, startFullTour, exitSection, closeTour,
    nextStep, prevStep,
    inShowcase,
  } = useTour();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const resizeTimer = useRef(null);
  const prevStepRef = useRef(-1);

  // Derive step info from currentStep (enriched by TourContext)
  const step = currentStep;
  const sectionColor = step?.sectionColor || BRAND.green;
  const Icon = step?.sectionIcon || null;
  const stepTarget = step?.target || null;
  const stepPage = step?.page || null;
  const stepPosition = step?.position || 'right';

  // ── Find target element and position ──────────────────────────────
  const findAndPosition = useCallback(() => {
    if (!stepTarget) {
      setTargetRect(null);
      setTooltipPos(null);
      return;
    }

    const el = document.querySelector(`[data-tour="${stepTarget}"]`);
    if (!el) {
      setTargetRect(null);
      setTooltipPos(null);
      return;
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    const rect = el.getBoundingClientRect();
    const padding = 8;
    const paddedRect = {
      top: rect.top - padding,
      left: rect.left - padding,
      right: rect.right + padding,
      bottom: rect.bottom + padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    };

    setTargetRect(paddedRect);
    setTooltipPos(positionTooltip(paddedRect, stepPosition));
  }, [stepTarget, stepPosition]);

  // ── Execute step actions (click field, etc.) ──────────────────────
  const executeStepAction = useCallback((action) => {
    if (!action) return;
    if (action === 'click-demo-field') {
      const demoCard = document.querySelector('[data-tour="field-list"] [data-demo="true"]')
        || document.querySelector('[data-tour="field-list"] > div > div');
      if (demoCard) demoCard.click();
    }
  }, []);

  // ── Navigate to page and find target when step changes ────────────
  useEffect(() => {
    // In showcase, ShowcaseCinema drives navigation and the clicks. The tour
    // still has tourActive=true (the showcase reuses it), so without the
    // inShowcase guard this effect ALSO ran executeStepAction('click-demo-field'),
    // which opens the first demo field -- on the cloned farm that is a healthy
    // block, not the stressed hero the cinema is deliberately opening. Two
    // clicks, and the tour's won: the decision beat read "no irrigation needed".
    if (!tourActive || inShowcase || !step || showSelector) return;

    const needsNavigation = stepPage && location.pathname !== stepPage &&
      !(stepPage === '/' && location.pathname.startsWith('/fields/'));

    if (needsNavigation) {
      setTransitioning(true);
      navigate(stepPage);
      const timer = setTimeout(() => {
        executeStepAction(step.action);
        setTimeout(findAndPosition, 500);
        setTransitioning(false);
      }, 800);
      return () => clearTimeout(timer);
    }

    executeStepAction(step.action);

    setTransitioning(true);
    const timer = setTimeout(() => {
      findAndPosition();
      setTransitioning(false);
    }, step.action ? 600 : (prevStepRef.current === -1 ? 100 : 300));

    prevStepRef.current = tourStep;
    return () => clearTimeout(timer);
  }, [tourActive, tourStep, step, stepPage, showSelector, navigate, location.pathname, findAndPosition, executeStepAction, inShowcase]);

  // ── Re-position on resize/scroll ─────────────────────────────────
  useEffect(() => {
    if (!tourActive || inShowcase || !stepTarget || showSelector) return;

    const handleReposition = () => {
      clearTimeout(resizeTimer.current);
      resizeTimer.current = setTimeout(findAndPosition, 100);
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
      clearTimeout(resizeTimer.current);
    };
  }, [tourActive, stepTarget, showSelector, findAndPosition, inShowcase]);

  // Derived render values
  const sectionProgress = useMemo(() => {
    if (!step) return 0;
    return ((step.stepInSection + 1) / step.sectionStepCount) * 100;
  }, [step]);

  const title = step ? (t(step.titleKey) || step.titleKey) : '';
  const desc = step ? (t(step.descKey) || step.descKey) : '';
  const sectionTitle = step ? (t(step.sectionTitleKey) || step.sectionTitleFallback || '') : '';
  const isFirstInSection = sectionStep === 0;
  const isLastInSection = step ? sectionStep === step.sectionStepCount - 1 : false;

  // ── Early return ─────────────────────────────────────────────────
  // ShowcaseCinema draws the demo. Two overlays over one screen is how you
  // get a caption card sitting on top of a caption card.
  if (!tourActive || inShowcase) return null;

  // ── Section Selector ─────────────────────────────────────────────
  if (showSelector) {
    return (
      <SectionSelector
        onStartSection={startSection}
        onPlayAll={startFullTour}
        onClose={closeTour}
        completedSections={completedSections}
        t={t}
      />
    );
  }

  // ── No active step (shouldn't happen, but safe) ──────────────────
  if (!step) return null;

  // ── Spotlight mode (tooltip next to highlighted element) ──────────
  const hasTarget = targetRect && !transitioning;

  // Determine next action button label
  let nextLabel;
  if (isLastInSection && playAllMode) {
    const currentIdx = TOUR_SECTIONS.findIndex((s) => s.id === activeSection);
    if (currentIdx >= TOUR_SECTIONS.length - 1) {
      nextLabel = t('finishTour') || 'Finish';
    } else {
      nextLabel = t('tourNextSection') || 'Next Section';
    }
  } else if (isLastInSection) {
    nextLabel = t('tourBackToSections') || 'Back to Sections';
  } else {
    nextLabel = t('nextStep') || 'Next';
  }

  return (
    <>
      {/* Spotlight cutout */}
      {hasTarget && (
        <Box sx={{
          position: 'fixed',
          top: targetRect.top, left: targetRect.left,
          width: targetRect.width, height: targetRect.height,
          borderRadius: '10px',
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
          zIndex: 9998,
          pointerEvents: 'none',
          transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        }} />
      )}

      {/* Fallback overlay when target not found */}
      {!hasTarget && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 9998, bgcolor: 'rgba(0,0,0,0.5)', pointerEvents: 'none' }} />
      )}

      {/* Click blocker on dimmed area */}
      <Box onClick={(e) => e.stopPropagation()}
        sx={{ position: 'fixed', inset: 0, zIndex: 9997, pointerEvents: 'auto', background: 'transparent' }} />

      {/* Re-enable pointer events on spotlight area */}
      {hasTarget && (
        <Box sx={{
          position: 'fixed',
          top: targetRect.top, left: targetRect.left,
          width: targetRect.width, height: targetRect.height,
          zIndex: 9999, pointerEvents: 'auto', borderRadius: '10px',
        }} />
      )}

      {/* Tooltip */}
      <Box sx={{
        position: 'fixed', zIndex: 10000, pointerEvents: 'auto', width: TOOLTIP_W,
        ...(tooltipPos && hasTarget
          ? { top: tooltipPos.top, left: tooltipPos.left, transform: tooltipPos.transform || 'none' }
          : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }),
        animation: 'tourTooltipIn 0.3s ease',
        '@keyframes tourTooltipIn': { from: { opacity: 0 }, to: { opacity: 1 } },
      }}>
        {tooltipPos?.arrow && tooltipPos.arrow !== 'none' && hasTarget && (
          <ArrowSvg direction={tooltipPos.arrow} color="#fff" />
        )}

        <Box sx={{
          bgcolor: '#fff', borderRadius: 3,
          boxShadow: '0 12px 48px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}>
          {/* Color bar */}
          <Box sx={{ height: 4, bgcolor: sectionColor }} />

          <Box sx={{ px: 2.5, py: 2 }}>
            {/* Section name + step counter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                label={`${sectionStep + 1} / ${step.sectionStepCount}`}
                size="small"
                sx={{
                  bgcolor: `${sectionColor}15`, color: sectionColor,
                  fontWeight: 700, fontSize: '0.92rem', height: 22,
                }}
              />
              <Typography sx={{ fontSize: '0.92rem', color: 'text.secondary', fontWeight: 500 }}>
                {sectionTitle}
              </Typography>
            </Box>

            {/* Title with icon */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {Icon && <Icon sx={{ fontSize: 20, color: sectionColor }} />}
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2, color: TEXT.primary }}>
                {title}
              </Typography>
            </Box>

            {/* Description */}
            <Typography sx={{ fontSize: '0.92rem', lineHeight: 1.65, color: '#4b5563', mb: 1.5 }}>
              {desc}
            </Typography>

            {/* Section progress bar */}
            <LinearProgress variant="determinate" value={sectionProgress}
              sx={{
                height: 2, borderRadius: 1, mb: 1.5,
                bgcolor: 'rgba(0,0,0,0.06)',
                '& .MuiLinearProgress-bar': { bgcolor: sectionColor, borderRadius: 1 },
              }}
            />

            {/* Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                size="small"
                startIcon={<ArrowBack sx={{ fontSize: '14px !important' }} />}
                onClick={playAllMode ? closeTour : exitSection}
                sx={{
                  color: 'text.secondary', textTransform: 'none',
                  fontSize: '0.9rem', minWidth: 'auto', px: 1,
                }}
              >
                {playAllMode
                  ? (t('skipTour') || 'Exit Tour')
                  : (t('tourExitSection') || 'Sections')}
              </Button>
              <Box sx={{ flex: 1 }} />
              {!isFirstInSection && (
                <IconButton size="small" onClick={prevStep} sx={{ color: 'text.secondary' }}>
                  <NavigateBefore fontSize="small" />
                </IconButton>
              )}

              <Button
                variant="contained"
                size="small"
                onClick={nextStep}
                endIcon={<NavigateNext sx={{ fontSize: '16px !important' }} />}
                sx={{
                  bgcolor: sectionColor,
                  '&:hover': { bgcolor: `${sectionColor}dd` },
                  textTransform: 'none', fontWeight: 600, fontSize: '0.9rem',
                  px: 2, py: 0.5, minHeight: 32, boxShadow: 'none',
                }}
              >
                {nextLabel}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default TourOverlay;
